import React from 'react';
import styled from '@emotion/native';
import { AsterisksText, BodyText, H1 } from '@/shared/components/StyledText';
import { accent, green, lightGreen, white } from '@/shared/styles/colors';
import { ProgressBar } from 'react-native-paper';
import useIsDesktop from '@/shared/hooks/useIsDesktop';
import { IsDesktopProps } from '@/shared/styles/types';
import { View } from 'react-native';

interface ProgressProps {
  progress: number;
  titleText: { [key: number]: string };
  descriptionText?: { [key: number]: string };
  descriptionContent?: { [key: number]: React.ReactNode };
  altStyle?: boolean;
  isRequired?: boolean;
  showCenteredView?: boolean;
  noteText?: { [key: number]: string };
}

interface StyledTextProps extends IsDesktopProps {
  altStyle?: boolean;
  isCenteredDesktop?: boolean;
}

const Progress: React.FC<ProgressProps> = ({
  progress,
  titleText,
  descriptionText,
  descriptionContent,
  altStyle = false,
  isRequired = true,
  showCenteredView = false,
}) => {
  const isDesktop = useIsDesktop();
  const isNotCenteredDesktop = isDesktop && !showCenteredView;
  const isCenteredDesktop = isDesktop && showCenteredView;

  const numberOfSteps = Object.keys(titleText).length - 1;

  return (
    <Container>
      <ColumnContainer isDesktop={isNotCenteredDesktop}>
        {titleText[progress] && (
          <StyledH1
            isDesktop={isNotCenteredDesktop}
            altStyle={altStyle}
            isCenteredDesktop={isCenteredDesktop}
          >
            {titleText[progress]}
          </StyledH1>
        )}
        <ProgressRow isDesktop={isNotCenteredDesktop}>
          <ProgressBarContainer isDesktop={isNotCenteredDesktop}>
            <StyledProgressBar
              progress={progress / numberOfSteps}
              color={accent}
            />
          </ProgressBarContainer>
          <StyledBodyText altStyle={altStyle}>
            {progress}/{numberOfSteps} Completed
          </StyledBodyText>
        </ProgressRow>
      </ColumnContainer>
      <ColumnContainer isDesktop={isNotCenteredDesktop}>
        {isRequired && <AsterisksText>*Required</AsterisksText>}
        <View>
          {descriptionContent?.[progress] ? (
            <ProgressBody isDesktop={isNotCenteredDesktop} altStyle={altStyle}>
              {descriptionContent[progress]}
            </ProgressBody>
          ) : descriptionText?.[progress] ? (
            <ProgressBody isDesktop={isNotCenteredDesktop} altStyle={altStyle}>
              {descriptionText[progress]}
            </ProgressBody>
          ) : null}
        </View>
      </ColumnContainer>
    </Container>
  );
};

const Container = styled.View({
  maxWidth: 477,
  paddingTop: 23,
});

const StyledProgressBar = styled(ProgressBar)<IsDesktopProps>(
  ({ isDesktop }) => ({
    height: 15,
    borderRadius: 9,
    backgroundColor: white,
    width: isDesktop ? 340 : 302,
  }),
);

const StyledBodyText = styled(BodyText)<{
  altStyle?: boolean;
}>(({ altStyle }) => ({
  fontSize: 11,
  lineHeight: 15.2,
  color: altStyle ? white : lightGreen,
}));

const ColumnContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  flexDirection: isDesktop ? 'column' : 'column-reverse',
  alignItems: isDesktop ? 'flex-start' : 'center',
  justifyContent: isDesktop ? 'flex-start' : 'center',
}));

const StyledH1 = styled(H1)<StyledTextProps>(
  ({ isDesktop, altStyle, isCenteredDesktop }) => ({
    fontSize: 45,
    lineHeight: 45,
    textTransform: 'uppercase',
    maxWidth: isDesktop ? 477 : '100%',
    marginBottom: 12,
    textAlign: isDesktop ? 'left' : 'center',
    color: altStyle ? white : green,
    marginTop: altStyle && !isCenteredDesktop ? '25%' : 0,
  }),
);

const ProgressBarContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  marginBottom: isDesktop ? 52 : 8,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: isDesktop ? 12 : 0,
  height: 15,
}));

const ProgressRow = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  flexDirection: isDesktop ? 'row' : 'column',
  alignItems: isDesktop ? 'flex-start' : 'center',
  marginTop: isDesktop ? 45 : 0,
  marginBottom: isDesktop ? 0 : 43,
}));

export const ProgressBody = styled(BodyText)<StyledTextProps>(
  ({ isDesktop, altStyle }) => ({
    color: altStyle ? white : lightGreen,
    maxWidth: isDesktop ? 'auto' : 308,
    textAlign: isDesktop ? 'left' : 'center',
    marginBottom: isDesktop ? 12 : 26,
    fontSize: 16,
  }),
);

export default Progress;
